"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  Input,
  Skeleton,
  type TanstackReactTable,
  Textarea,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  FileUpIcon,
  LinkIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import type { ReconciliationItem } from "../api/reconciliation-client";
import {
  useMatchReconciliationMutation,
  useReconciliationListQuery,
  useReconciliationStatsQuery,
  useResolveReconciliationMutation,
} from "../api/reconciliation-queries";

// --- Constants ---

const SOURCE_TYPE_LABELS: Record<string, string> = {
  bank_statement: "ໃບແຈ້ງຍອດທະນາຄານ",
  payment_provider: "ຜູ້ໃຫ້ບໍລິການຊຳລະ",
  manual: "ມານວລ",
};

const STATUS_STYLE: Record<
  string,
  {
    variant: "default" | "secondary" | "outline" | "destructive";
    label: string;
  }
> = {
  pending: { variant: "secondary", label: "ລໍຖ້າ" },
  matched: { variant: "default", label: "ຈັບຄູ່ແລ້ວ" },
  unmatched: { variant: "destructive", label: "ບໍ່ພົບຄູ່" },
  discrepancy: { variant: "destructive", label: "ຍອດບໍ່ກົງ" },
  resolved: { variant: "outline", label: "ແກ້ໄຂແລ້ວ" },
};

const STATUS_OPTIONS = [
  { value: "all", label: "ສະຖານະທັງໝົດ" },
  { value: "pending", label: "ລໍຖ້າ" },
  { value: "matched", label: "ຈັບຄູ່ແລ້ວ" },
  { value: "unmatched", label: "ບໍ່ພົບຄູ່" },
  { value: "discrepancy", label: "ຍອດບໍ່ກົງ" },
  { value: "resolved", label: "ແກ້ໄຂແລ້ວ" },
] as const;

const SOURCE_OPTIONS = [
  { value: "all", label: "ແຫຼ່ງທັງໝົດ" },
  { value: "bank_statement", label: "ໃບແຈ້ງຍອດ" },
  { value: "payment_provider", label: "Payment Provider" },
  { value: "manual", label: "ມານວລ" },
] as const;

// --- Main Page ---

export function ReconciliationPage() {
  const nav = useNavigate({ from: "/app/wallet/reconciliation" });
  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ການຕິດຕາມກັນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ການຕິດຕາມກັນ</h2>
            <p className="text-muted-foreground">
              ນຳເຂົ້າ, ຈັບຄູ່ ແລະ ຈັດການຄວາມແຕກຕ່າງຂອງລາຍການ
            </p>
          </div>
          <Button
            onClick={() => nav({ to: "/app/wallet/reconciliation/import" })}
          >
            <FileUpIcon className="size-4" /> ນຳເຂົ້າລາຍການ
          </Button>
        </div>

        <StatsCards />
        <ReconciliationTable />
      </Main>
    </>
  );
}

// --- Stats ---

function StatsCards() {
  const { data: stats, isLoading } = useReconciliationStatsQuery();

  if (isLoading) {
    return (
      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[100px] rounded-xl" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <SummaryCard
        title="ລໍຖ້າຈັບຄູ່"
        icon={ClockIcon}
        color={stats.pending > 0 ? "orange" : "muted"}
        items={[{ label: "", value: stats.pending.toLocaleString() }]}
        subtitle="ລາຍການ pending"
      />
      <SummaryCard
        title="ຈັບຄູ່ແລ້ວ"
        icon={CheckCircleIcon}
        color="success"
        items={[{ label: "", value: stats.matched.toLocaleString() }]}
        subtitle="ລາຍການ matched"
      />
      <SummaryCard
        title="ບໍ່ພົບຄູ່"
        icon={SearchIcon}
        color={stats.unmatched > 0 ? "destructive" : "muted"}
        items={[{ label: "", value: stats.unmatched.toLocaleString() }]}
        subtitle="ລາຍການ unmatched"
      />
      <SummaryCard
        title="ຍອດບໍ່ກົງ"
        icon={AlertTriangleIcon}
        color={stats.discrepancy > 0 ? "destructive" : "muted"}
        items={[{ label: "", value: stats.discrepancy.toLocaleString() }]}
        subtitle="ລາຍການ discrepancy"
      />
    </div>
  );
}

// --- Table ---

function ReconciliationTable() {
  const nav = useNavigate({ from: "/app/wallet/reconciliation" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/wallet/reconciliation",
  }) as OffsetPageQueryDTO;

  const { data, isLoading } = useReconciliationListQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const statusFilter =
    (findCondition(filters, "status")?.value as string) || "all";
  const sourceFilter =
    (findCondition(filters, "sourceType")?.value as string) || "all";
  const hasFilter = statusFilter !== "all" || sourceFilter !== "all";

  function handleFilterChange(field: string, value: string) {
    let next: FilterConditionDTO[] | undefined = filters;
    if (value === "all") {
      next = removeConditions(filters, field);
    } else {
      next = upsertCondition(filters, { field, op: "eq", value });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  }

  const columns: TanstackReactTable.ColumnDef<ReconciliationItem>[] = [
    createSortableColumn<ReconciliationItem>("createdAt", "ວັນທີນຳເຂົ້າ", {
      size: 160,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatDateTimeLocal(row.original.createdAt)}
        </span>
      ),
    }),
    {
      id: "sourceType",
      header: "ແຫຼ່ງ",
      cell: ({ row }) => (
        <span className="text-sm">
          {SOURCE_TYPE_LABELS[row.original.sourceType] ??
            row.original.sourceType}
        </span>
      ),
      size: 140,
    },
    {
      id: "sourceRef",
      header: "ອ້າງອີງ",
      cell: ({ row }) => (
        <span className="truncate font-mono text-xs">
          {row.original.sourceRef}
        </span>
      ),
      size: 160,
    },
    {
      id: "amount",
      header: "ຈຳນວນເງິນ",
      cell: ({ row }) => (
        <span className="font-semibold tabular-nums">
          {formatPrice(row.original.amount, row.original.currency)}
        </span>
      ),
      size: 150,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const s = STATUS_STYLE[row.original.status] ?? {
          variant: "outline" as const,
          label: row.original.status,
        };
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
      size: 120,
    },
    {
      id: "discrepancy",
      header: "ຜິດປົກກະຕິ",
      cell: ({ row }) => {
        if (row.original.discrepancyAmount == null)
          return <span className="text-muted-foreground">—</span>;
        return (
          <span className="font-semibold text-destructive tabular-nums">
            {formatPrice(row.original.discrepancyAmount, row.original.currency)}
          </span>
        );
      },
      size: 130,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => <RecordActions record={row.original} />,
      size: 160,
    },
  ];

  return (
    <div className="flex flex-col rounded-xl border bg-card pt-2">
      <div className="mb-4 flex flex-col gap-2 px-2">
        <div className="flex items-center justify-between gap-4 max-sm:flex-col">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <SimpleSelect
              value={statusFilter}
              onValueChange={(v) => handleFilterChange("status", v)}
              placeholder="ສະຖານະ"
              className="h-8 w-full sm:w-40"
              options={STATUS_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
            <SimpleSelect
              value={sourceFilter}
              onValueChange={(v) => handleFilterChange("sourceType", v)}
              placeholder="ແຫຼ່ງ"
              className="h-8 w-full sm:w-48"
              options={SOURCE_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
          </div>
          {hasFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                nav({
                  search: { ...search, filters: undefined, offset: 0 },
                })
              }
              className="max-sm:w-full"
            >
              <XIcon className="size-4" /> ລ້າງ
            </Button>
          )}
        </div>
      </div>

      <DataTable<ReconciliationItem, unknown>
        noDataMessage="ບໍ່ພົບລາຍການ"
        isLoading={isLoading}
        columns={columns}
        data={data?.data ?? []}
        offset={search.offset ?? 0}
        limit={search.limit ?? 20}
        totalCount={data?.meta?.total ?? 0}
        onPaginationChange={(p) =>
          nav({ search: { ...search, offset: p.offset, limit: p.limit } })
        }
        sortBy={search.sort ? search.sort[0]?.field : undefined}
        sortOrder={
          search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
        }
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          nav({
            search: {
              ...search,
              sort: [
                {
                  field: sorting[0]?.id as string,
                  dir: sorting[0]?.desc ? "desc" : "asc",
                },
              ],
            },
          });
        }}
      />
    </div>
  );
}

// --- Actions ---

function RecordActions({ record }: { record: ReconciliationItem }) {
  const matchMutation = useMatchReconciliationMutation();
  const resolveMutation = useResolveReconciliationMutation();
  const [txId, setTxId] = useState("");
  const [notes, setNotes] = useState("");

  const canMatch = record.status === "pending" || record.status === "unmatched";
  const canResolve =
    record.status === "discrepancy" || record.status === "unmatched";

  if (!canMatch && !canResolve) {
    return (
      <span className="text-muted-foreground text-xs">
        {record.reconciledByName ?? "—"}
      </span>
    );
  }

  return (
    <div className="flex gap-1">
      {canMatch && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm">
              <LinkIcon className="size-3" /> ຈັບຄູ່
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>ຈັບຄູ່ກັບທຸລະກຳ</AlertDialogTitle>
              <AlertDialogDescription>
                ປ້ອນ Transaction ID ເພື່ອຈັບຄູ່ກັບລາຍການນີ້ (ຈຳນວນເງິນ:{" "}
                {formatPrice(record.amount, record.currency)})
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              placeholder="Transaction ID (UUID)"
              value={txId}
              onChange={(e) => setTxId(e.target.value)}
              className="mt-2"
            />
            <AlertDialogFooter>
              <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
              <AlertDialogAction
                disabled={!txId.trim()}
                onClick={() =>
                  matchMutation.mutate({
                    id: record.id,
                    body: { matchedTransactionId: txId.trim() },
                  })
                }
              >
                ຈັບຄູ່
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {canResolve && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm">
              <CheckCircleIcon className="size-3" /> ແກ້ໄຂ
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>ແກ້ໄຂລາຍການ</AlertDialogTitle>
              <AlertDialogDescription>
                ບັນທຶກການແກ້ໄຂ (adjust, ຕິດຕໍ່ທະນາຄານ ฯลฯ)
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Textarea
              placeholder="ບັນທຶກ..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-2"
            />
            <AlertDialogFooter>
              <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
              <AlertDialogAction
                disabled={!notes.trim()}
                onClick={() =>
                  resolveMutation.mutate({
                    id: record.id,
                    body: { notes: notes.trim() },
                  })
                }
              >
                ແກ້ໄຂ
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
